/**
 * System-like terminal commands that mimic Unix/Linux commands.
 * These commands provide a familiar shell experience for developers.
 *
 * @module terminal/commands/system
 */
import type { CommandResult, CommandContext } from './types';
import { THEMES } from '../themes';

/**
 * System commands that simulate Unix-like behavior.
 * Provides familiar commands for developers: whoami, pwd, ls, cat, etc.
 *
 * @example
 * ```
 * whoami        - Show current user (guest@ameta)
 * pwd           - Show working directory
 * ls            - List files in directory
 * cat <file>    - Read file contents
 * date          - Show current date/time
 * uptime        - Show system uptime
 * neofetch      - Display system info
 * sudo          - Nice try!
 * ping          - PONG!
 * rm -rf /      - Permission denied :)
 * ```
 */
export const systemCommands: Record<
	string,
	(args: string[], context: CommandContext) => CommandResult | null
> = {
	whoami: () => ({ type: 'text', lines: ['guest@ameta'] }),
	pwd: () => ({ type: 'text', lines: ['/home/guest/ameta'] }),
	ls: () => ({
		type: 'text',
		lines: [
			'total 5',
			'drwxr-xr-x  about-us.md',
			'drwxr-xr-x  services/',
			'drwxr-xr-x  projects/',
			'-rw-r--r--  contact.txt',
			'-rw-------  secrets.enc'
		]
	}),
	date: () => ({ type: 'text', lines: [new Date().toString()] }),
	uptime: () => {
		const days = Math.floor(Math.random() * 365) + 42;
		const hours = Math.floor(Math.random() * 24);
		const mins = Math.floor(Math.random() * 60);
		return {
			type: 'text',
			lines: [
				`up ${days} days, ${hours}:${String(mins).padStart(2, '0')}, 1 user, load average: 0.42, 0.69, 1.337`
			]
		};
	},
	sudo: () => ({ type: 'text', lines: ['Nice try, but you already have maximum privileges.'] }),
	ping: () => ({ type: 'text', lines: ["PONG! 0ms latency (it's all local)"] }),
	rm: (args) => {
		if (args.join(' ').includes('-rf')) {
			return {
				type: 'ascii',
				content: [
					'⚠️  WARNING ⚠️',
					'┌─────────────────────────────────────┐',
					'│  rm -rf / detected!                  │',
					'│                                      │',
					'│  ╔══════════════════════════════╗    │',
					'│  ║  PERMISSION DENIED           ║    │',
					'│  ║  Nice try, but we actually   ║    │',
					'│  ║  like our files.             ║    │',
					'│  ╚══════════════════════════════╝    │',
					'│                                      │',
					'│  This incident has been logged.      │',
					'│  (just kidding... or are we?)        │',
					'└─────────────────────────────────────┘'
				].join('\n')
			};
		}
		return { type: 'text', lines: ['rm: missing operand'] };
	},
	neofetch: (_, context) => ({
		type: 'text',
		lines: [
			'       _____          guest@ameta',
			'      /     \\         ------------',
			'     | () () |        OS: AmetaOS 2.0',
			'      \\  ^  /         Host: Terminal v2.0',
			'       |||||          Kernel: 4.2.0-ameta',
			'       |||||          Shell: ameta-sh',
			`                      Theme: ${THEMES[context.currentTheme].name}`,
			'                      Terminal: 80x24'
		]
	}),
	cat: (args) => {
		const file = args.join(' ').toLowerCase();
		if (file === 'contact.txt' || file === 'kontakt.txt') {
			return {
				type: 'text',
				lines: [
					'# contact.txt',
					'',
					'Email:    hello@ameta.no',
					'Phone:    +47 466 11 996',
					'Address:  Kjøita 40, 4630 Kristiansand, Norway',
					'Web:      https://ameta.no',
					'LinkedIn: linkedin.com/company/ameta-no',
					'',
					'We respond within 24 hours.'
				]
			};
		}
		if (file === 'about-us.md' || file === 'om-oss.md') {
			return {
				type: 'text',
				lines: [
					'# About Ameta',
					'',
					'Ameta is an industrial technology company based in Kristiansand, Norway.',
					'We specialize in connecting industrial equipment, building data platforms,',
					'and creating predictive maintenance solutions.',
					'',
					'Founded in 2019, we solve the problems others say no to.',
					'From legacy PLC systems to modern cloud platforms - we bridge the gap.'
				]
			};
		}
		if (file === 'services' || file === 'tjenester') {
			return {
				type: 'text',
				lines: [
					'# Services',
					'',
					'- Industrial IoT & Data Collection',
					'- Predictive Maintenance Systems',
					'- OEE & Production Monitoring',
					'- Systems Integration (OPC-UA, Modbus, MQTT)',
					'- Custom Dashboard Development',
					'- Cloud Infrastructure & DevOps',
					'- AI/ML Solutions for Industry'
				]
			};
		}
		if (file === 'projects' || file === 'prosjekter') {
			return {
				type: 'text',
				lines: [
					'# Projects',
					'',
					'- Real-time production monitoring dashboards',
					'- Predictive maintenance for industrial equipment',
					'- Legacy system integration platforms',
					'- IoT sensor networks & data pipelines',
					'- Custom ERP integrations',
					'',
					'Contact us for case studies: hello@ameta.no'
				]
			};
		}
		if (file === 'secrets' || file === 'secrets.enc' || file === 'hemmeligheter') {
			return {
				type: 'text',
				lines: [
					'ACCESS DENIED',
					'',
					'Nice try. This file is encrypted with AES-256.',
					"You'd need approximately 3.31 x 10^56 years to brute force it.",
					'Maybe try "help" instead?'
				]
			};
		}
		if (!file) {
			return { type: 'text', lines: ['cat: missing file operand'] };
		}
		return { type: 'text', lines: [`cat: ${file}: No such file or directory`] };
	}
};
